import React, { useEffect, useRef, useState } from 'react';

/**
 * A NUMBER BOX THAT CAN ACTUALLY BE EMPTIED.
 *
 * THE BUG THIS FIXES -- reported on the Agreed Total OTS field: pressing
 * backspace on the last digit put a 0 back in the box, and HOLDING backspace
 * left it sitting at 0 forever. It could not be cleared to retype a figure.
 *
 * WHY IT HAPPENED. Every amount field in the app was written like this:
 *
 *     value={someNumber}
 *     onChange={(e) => setSomeNumber(Number(e.target.value))}
 *
 * A number has no empty. Delete the last character and the browser reports
 * `''`; `Number('')` is `0`; the box re-renders showing "0". Hold backspace
 * and you delete that 0, which produces `''`, which becomes 0 again -- the
 * figure "continuously returns", exactly as described.
 *
 * It was on NINE fields across seven screens, Collection Amount among them.
 *
 * THE FIX. This control keeps what the person TYPED as text, which can be
 * empty, and reports a number upward. An empty box reports 0 without
 * printing 0, so the person can clear it and type a fresh figure.
 *
 * Two details that matter:
 *
 *   - It does not fight the typist. The text is only re-synced from the
 *     prop when the prop means something different from what is on screen,
 *     so typing "1", "12", "125" is never interrupted, and a half-typed
 *     "12." survives until the next character.
 *
 *   - It is `inputMode="decimal"`, not `type="number"`. A number input
 *     silently discards what it considers invalid, which is the other half
 *     of why clearing it behaved so strangely, and its scroll-wheel
 *     behaviour can change an amount by accident while scrolling a long
 *     form. Phones still get the numeric keypad.
 */
interface AmountInputProps {
  value: number;
  onValueChange: (n: number) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  /** Allow a decimal point. Off by default -- rupee figures here are whole. */
  allowDecimal?: boolean;
  'aria-label'?: string;
}

/** What a typed string means as a number. Empty means zero. */
export function amountFromText(raw: string): number {
  const t = (raw || '').trim();
  if (t === '' || t === '.' || t === '-') return 0;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onValueChange,
  className,
  placeholder,
  id,
  disabled,
  allowDecimal = false,
  'aria-label': ariaLabel,
}) => {
  // What is on screen. Starts blank for 0 so the placeholder shows through
  // rather than a 0 the person has to delete before typing.
  const [text, setText] = useState<string>(() => (value ? String(value) : ''));
  const lastReported = useRef<number>(value);

  useEffect(() => {
    // Only pull the prop back into the box when it genuinely disagrees with
    // what is shown -- e.g. the account changed, or a settlement was loaded.
    // Without this guard, every keystroke would be overwritten.
    if (value === lastReported.current) return;
    setText(value ? String(value) : '');
    lastReported.current = value;
  }, [value]);

  const handle = (raw: string) => {
    const cleaned = allowDecimal
      ? raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
      : raw.replace(/[^0-9]/g, '');
    setText(cleaned);
    const n = amountFromText(cleaned);
    lastReported.current = n;
    onValueChange(n);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      autoComplete="off"
      disabled={disabled}
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => handle(e.target.value)}
      className={className}
    />
  );
};
